import { useEffect, useRef } from "react";
import { useAudioContext } from "../hooks/useAudioContext";

interface ClickAudioProps {
  isRunning: boolean;
  currentBeat: number;
  isMuted: boolean;
  volume: number;
  stressFirstBeat: boolean;
  stressedBeats: number[];
  subdivision: number;
  currentSubBeat: number; // New prop for subdivision beat tracking
  bpm: number;
  beatsPerBar: number;
  noteValue: number;
  reverbAmount?: number;
  onReverbAmountChange?: (amount: number) => void;
  clickStyle?: string;
  isChangingBar?: boolean;
}

export const ClickAudio = ({
  isRunning,
  currentBeat,
  isMuted,
  volume,
  stressFirstBeat,
  stressedBeats,
  subdivision,
  currentSubBeat,
  bpm,
  beatsPerBar,
  noteValue,
  reverbAmount,
  onReverbAmountChange,
  clickStyle = "Quartz",
  isChangingBar = false,
}: ClickAudioProps) => {
  const { audioContext, scheduleSound, preloadClickSet, setReverbAmount } =
    useAudioContext();

  // Scheduler refs
  const nextNoteTimeRef = useRef<number>(0); // in AudioContext seconds
  const nextBeatCountRef = useRef<number>(0); // 1..beatsPerBar
  const nextSubBeatRef = useRef<number>(0); // 1..subdivision
  const schedulerTimerRef = useRef<number | null>(null);
  const pendingTicksRef = useRef<
    Array<{ time: number; beat: number; subBeat: number }>
  >([]);
  const uiDispatchRafRef = useRef<number | null>(null);
  const suppressBarEventsRef = useRef<boolean>(false);
  const lastBarEventTimeRef = useRef<number>(0);
  const lastBpmRef = useRef<number>(bpm);
  const lastBeatsPerBarRef = useRef<number>(beatsPerBar);
  const lastNoteValueRef = useRef<number>(noteValue);

  // Update reverb amount when it changes
  useEffect(() => {
    if (reverbAmount !== undefined) {
      setReverbAmount(reverbAmount);
    }
  }, [reverbAmount, setReverbAmount]);

  // Preload samples for current click style
  useEffect(() => {
    void preloadClickSet(clickStyle);
  }, [clickStyle, preloadClickSet]);

  // Create refs to store current prop values
  const currentBpmRef = useRef(bpm);
  const currentBeatsPerBarRef = useRef(beatsPerBar);
  const currentNoteValueRef = useRef(noteValue);
  const currentSubdivisionRef = useRef(subdivision);
  const currentClickStyleRef = useRef(clickStyle);
  const currentStressFirstBeatRef = useRef(stressFirstBeat);
  const currentStressedBeatsRef = useRef(stressedBeats);
  const currentVolumeRef = useRef(volume);
  const currentIsMutedRef = useRef(isMuted);

  // Update refs when props change
  useEffect(() => {
    currentBpmRef.current = bpm;
    currentBeatsPerBarRef.current = beatsPerBar;
    currentNoteValueRef.current = noteValue;
    currentSubdivisionRef.current = subdivision;
    currentClickStyleRef.current = clickStyle;
    currentStressFirstBeatRef.current = stressFirstBeat;
    currentStressedBeatsRef.current = stressedBeats;
    currentVolumeRef.current = volume;
    currentIsMutedRef.current = isMuted;
  }, [
    bpm,
    beatsPerBar,
    noteValue,
    subdivision,
    clickStyle,
    stressFirstBeat,
    stressedBeats,
    volume,
    isMuted,
  ]);

  // Web Audio lookahead scheduler for precise timing
  useEffect(() => {
    if (!audioContext) return;

    const scheduleAheadTime = 0.12; // seconds to schedule ahead
    const lookaheadMs = 25; // scheduler tick

    const scheduleTick = () => {
      if (!audioContext) return;
      const now = audioContext.currentTime;
      // Use refs to get current values (always up-to-date)

      // Recalculate interval with current values
      const quarterNoteSec = 60 / currentBpmRef.current;
      const noteMultiplier = 4 / currentNoteValueRef.current;
      const baseSec = quarterNoteSec * noteMultiplier;
      const effectiveSubdivision = Math.max(1, currentSubdivisionRef.current);
      const subBeatInterval = baseSec / effectiveSubdivision;

      // Initialize next times if starting
      if (nextNoteTimeRef.current <= 0) {
        nextNoteTimeRef.current = now + 0.03; // small offset to start cleanly
        nextSubBeatRef.current = 1;
        nextBeatCountRef.current = 1;
      }

      while (nextNoteTimeRef.current < now + scheduleAheadTime) {
        if (!isRunning) break;

        // Don't schedule audio if we're in the middle of a parameter change
        if (suppressBarEventsRef.current) {
          break;
        }

        // Don't schedule audio if the metronome is stopping
        if (!schedulerTimerRef.current) {
          break;
        }

        // Use refs for click style and stress first beat to get real-time updates
        const lowerStyle = currentClickStyleRef.current.toLowerCase();
        const stylePrefix = `/click_${lowerStyle}`;
        const fileExt = lowerStyle === "drop" ? ".wav" : ".mp3";
        let soundUrl: string;
        const isMainBeat = nextSubBeatRef.current === 1;
        if (isMainBeat) {
          if (
            currentStressFirstBeatRef.current &&
            currentStressedBeatsRef.current.includes(nextBeatCountRef.current)
          ) {
            soundUrl = `${stylePrefix}_hi${fileExt}`;
          } else {
            soundUrl = `${stylePrefix}_mid${fileExt}`;
          }
        } else {
          soundUrl = `${stylePrefix}_lo${fileExt}`;
        }

        const scheduledTime = nextNoteTimeRef.current;
        const scheduledBeat = nextBeatCountRef.current;
        const scheduledSubBeat = nextSubBeatRef.current;

        // Use volume 0 when muted, otherwise use current volume
        const effectiveVolume = currentIsMutedRef.current
          ? 0
          : currentVolumeRef.current;
        void scheduleSound(soundUrl, effectiveVolume, scheduledTime);
        // Queue UI update at same scheduled audio time
        pendingTicksRef.current.push({
          time: scheduledTime,
          beat: scheduledBeat,
          subBeat: scheduledSubBeat,
        });

        // Advance counters/time using current values
        nextNoteTimeRef.current += subBeatInterval;
        nextSubBeatRef.current =
          (nextSubBeatRef.current % effectiveSubdivision) + 1;
        if (nextSubBeatRef.current === 1) {
          nextBeatCountRef.current =
            (nextBeatCountRef.current %
              Math.max(1, currentBeatsPerBarRef.current)) +
            1;
        }
      }
    };

    const pumpUiTicks = () => {
      if (!audioContext) return;
      const now = audioContext.currentTime;
      const epsilon = 0.002; // 2ms tolerance for rAF vs audio
      while (
        pendingTicksRef.current.length &&
        pendingTicksRef.current[0].time <= now + epsilon
      ) {
        const tick = pendingTicksRef.current.shift()!;
        // Dispatch bar reset BEFORE audio-tick so UI reset happens first, then coloring of beat 1
        if (tick.subBeat === 1 && tick.beat === 1) {
          const now = audioContext.currentTime;
          const timeSinceLastBarEvent = now - lastBarEventTimeRef.current;

          // Prevent bar events that are too close together (less than 0.5 seconds)
          // This prevents rapid bar events from scheduler restarts
          if (timeSinceLastBarEvent < 0.5 && lastBarEventTimeRef.current > 0) {
          } else if (suppressBarEventsRef.current) {
          } else {
            lastBarEventTimeRef.current = now;
            window.dispatchEvent(new CustomEvent("bar"));
          }
        }
        window.dispatchEvent(
          new CustomEvent("audio-tick", {
            detail: { beat: tick.beat, subBeat: tick.subBeat },
          })
        );
      }
      uiDispatchRafRef.current = window.requestAnimationFrame(pumpUiTicks);
    };

    // Start/stop scheduler based on running state
    if (isRunning) {
      // Always check if scheduler is actually running
      if (!schedulerTimerRef.current) {
        // Reset sequencing anchors on start
        nextNoteTimeRef.current = 0;
        nextSubBeatRef.current = 1;
        nextBeatCountRef.current = 1;
        pendingTicksRef.current = [];
        suppressBarEventsRef.current = false;
        lastBarEventTimeRef.current = 0; // Reset bar event timing

        // Initialize tracking refs with current values
        lastBpmRef.current = currentBpmRef.current;
        lastBeatsPerBarRef.current = currentBeatsPerBarRef.current;
        lastNoteValueRef.current = currentNoteValueRef.current;
        const timerId = window.setInterval(scheduleTick, lookaheadMs);
        schedulerTimerRef.current = timerId;
        if (uiDispatchRafRef.current == null) {
          uiDispatchRafRef.current = window.requestAnimationFrame(pumpUiTicks);
        }
      } else {
        // Check if parameters have changed and restart scheduler if needed
        if (
          currentBpmRef.current !== lastBpmRef.current ||
          currentBeatsPerBarRef.current !== lastBeatsPerBarRef.current ||
          currentNoteValueRef.current !== lastNoteValueRef.current
        ) {
          // Calculate the new timing interval
          const quarterNoteSec = 60 / currentBpmRef.current;
          const noteMultiplier = 4 / currentNoteValueRef.current;
          const baseSec = quarterNoteSec * noteMultiplier;
          const effectiveSubdivision = Math.max(
            1,
            currentSubdivisionRef.current
          );
          const subBeatInterval = baseSec / effectiveSubdivision;

          // CRITICAL FIX: When bar changes, Beat 1 was already played (which triggered this change)
          // So we need to start scheduling from Beat 2, not Beat 1 again
          const now = audioContext.currentTime;
          nextNoteTimeRef.current = now + subBeatInterval;
          nextSubBeatRef.current = 1;
          // Start from beat 2 since beat 1 of the new bar already played
          // (it was the beat that triggered the bar change event)
          nextBeatCountRef.current = 2;

          // Clear pending UI ticks that were scheduled with old timing
          pendingTicksRef.current = [];

          // Update the refs to track the new values - scheduler continues running
          lastBpmRef.current = currentBpmRef.current;
          lastBeatsPerBarRef.current = currentBeatsPerBarRef.current;
          lastNoteValueRef.current = currentNoteValueRef.current;
        }
      }
    } else {
      if (schedulerTimerRef.current) {
        clearInterval(schedulerTimerRef.current);
        schedulerTimerRef.current = null;
      }
      if (uiDispatchRafRef.current != null) {
        cancelAnimationFrame(uiDispatchRafRef.current);
        uiDispatchRafRef.current = null;
      }
      pendingTicksRef.current = [];
    }

    return () => {
      // Only cleanup if we're actually stopping the metronome, not just changing parameters
      if (!isRunning) {
        if (schedulerTimerRef.current) {
          clearInterval(schedulerTimerRef.current);
          schedulerTimerRef.current = null;
        }
        if (uiDispatchRafRef.current != null) {
          cancelAnimationFrame(uiDispatchRafRef.current);
          uiDispatchRafRef.current = null;
        }
      } else {
      }
    };
  }, [audioContext, isRunning, scheduleSound, isChangingBar]);

  return null;
};
